import React, { useMemo } from 'react';
import { flushSync } from 'react-dom';
import { addWeeks, startOfWeek, subWeeks } from 'date-fns';
import type { Timesheet, TimesheetEntry } from '@/types/timesheet';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';

export interface UseTimesheetWeekNavigationParams {
  currentWeekStart: Date;
  setCurrentWeekStart: React.Dispatch<React.SetStateAction<Date>>;
  weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  organizationSettings: OrganisationTimesheetSettings | undefined;
  currentTimesheet: Timesheet;
  entriesRef: React.RefObject<TimesheetEntry[]>;
  updateTimesheet: (timesheet: Timesheet) => void;
  isViewingOtherResource: boolean;
  isReadOnly: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  autoSaveTimerRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
  setLastSaved: React.Dispatch<React.SetStateAction<Date | null>>;
}

export interface UseTimesheetWeekNavigationResult {
  canNavigatePrev: boolean;
  canNavigateNext: boolean;
  navigateWeek: (direction: 'prev' | 'next') => void;
  navigateToDate: (date: Date) => void;
}

/**
 * Week navigation, including the save-before-navigate guard that persists unsaved
 * entries to the correct week before the week changes.
 */
export function useTimesheetWeekNavigation({
  currentWeekStart,
  setCurrentWeekStart,
  weekStartDay,
  organizationSettings,
  currentTimesheet,
  entriesRef,
  updateTimesheet,
  isViewingOtherResource,
  isReadOnly,
  isSaving,
  isSubmitting,
  autoSaveTimerRef,
  setLastSaved,
}: UseTimesheetWeekNavigationParams): UseTimesheetWeekNavigationResult {
  const canNavigatePrev = useMemo(() => {
    const limit = organizationSettings?.historicalEntryLimit ?? 4;
    const earliest = subWeeks(startOfWeek(new Date(), { weekStartsOn: weekStartDay }), limit);
    return subWeeks(currentWeekStart, 1) >= earliest;
  }, [currentWeekStart, organizationSettings?.historicalEntryLimit, weekStartDay]);

  const canNavigateNext = useMemo(() => {
    const setting = organizationSettings?.allowFutureDatedEntries ?? '2weeks';
    if (setting === 'unlimited') return true;
    const now = startOfWeek(new Date(), { weekStartsOn: weekStartDay });
    if (setting === 'no') return addWeeks(currentWeekStart, 1) <= now;
    const weeksAhead = setting === '1week' ? 1 : setting === '2weeks' ? 2 : 4;
    return addWeeks(currentWeekStart, 1) <= addWeeks(now, weeksAhead);
  }, [currentWeekStart, organizationSettings?.allowFutureDatedEntries, weekStartDay]);

  /**
   * Persists unsaved entries to the week being left. Uses entriesRef.current (not a
   * closed-over `entries`) so we always get the very latest state — even if the user
   * selected a project/cost centre and navigated in the same event-loop tick.
   */
  const saveBeforeNavigate = () => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);

    const latestEntries = entriesRef.current;
    const dirtyNow = !isReadOnly && (
      latestEntries.length !== currentTimesheet.entries.length ||
      JSON.stringify(latestEntries) !== JSON.stringify(currentTimesheet.entries)
    );
    if (dirtyNow && !isSaving && !isSubmitting) {
      const updated = { ...currentTimesheet, entries: latestEntries, status: 'draft' as const };
      // flushSync forces React to commit the context state update (timesheets array)
      // BEFORE setCurrentWeekStart runs. Without this, React 18 batches both state
      // updates into one render — currentTimesheet recomputes for the NEW week before
      // the save lands, causing the sync effect to wipe local entries to [].
      flushSync(() => {
        updateTimesheet(updated);
      });
      setLastSaved(new Date());
    }
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    if (isViewingOtherResource) return;
    if (direction === 'prev' && !canNavigatePrev) return;
    if (direction === 'next' && !canNavigateNext) return;

    saveBeforeNavigate();

    setCurrentWeekStart(prev =>
      direction === 'prev' ? subWeeks(prev, 1) : addWeeks(prev, 1)
    );
  };

  const navigateToDate = (date: Date) => {
    if (isViewingOtherResource) return;

    saveBeforeNavigate();

    const newWeekStart = startOfWeek(date, { weekStartsOn: weekStartDay });
    setCurrentWeekStart(newWeekStart);
  };

  return { canNavigatePrev, canNavigateNext, navigateWeek, navigateToDate };
}
